import React, { FC, PropsWithChildren, createContext, useCallback, useContext } from 'react';
import { useDeepMemo } from '../hooks/useCompare';
import udsMessages from './uds.intl.json';

export type Messages = typeof udsMessages;

export type UdsMessages = FirstDepthOptionalDeeperRequired<Messages>;

export type IntlUdsContextProps = {
  udsMessages: Messages;
  format: (message: string, values: Record<string, string>) => string;
};

export const IntlUdsContext = createContext<IntlUdsContextProps>({
  udsMessages,
  format: () => ''
});

export interface UdsIntlProps {
  messages: UdsMessages | undefined;
  format: ((message: string, values: Record<string, string>) => string) | undefined;
}

export const UdsIntlProvider: FC<PropsWithChildren<UdsIntlProps>> = ({ children, messages, format }) => {
  const udsFormat = useCallback(
    (message: string, values: Record<string, string>) =>
      message.replace(/{([^{}]*)}/g, (match, key) => values[key] || match),
    []
  );
  const value = useDeepMemo(
    () => ({ udsMessages: { ...udsMessages, ...messages }, format: format || udsFormat }),
    [messages, format, udsFormat]
  );

  return <IntlUdsContext.Provider value={value}>{children}</IntlUdsContext.Provider>;
};

export function useIntlUds() {
  const { udsMessages, format } = useContext<IntlUdsContextProps>(IntlUdsContext);

  return { intl: udsMessages, format };
}
